/* iter-221 · MilestoneProgressionSheet
 *
 * A lightweight, Apple-calm per-step progression sheet. Opens when
 * the agent taps a milestone in the operational journey rail and
 * lets them drive that one step forward:
 *
 *   • see current status + (optional) target date
 *   • set / update / clear the target date
 *   • drop a calm operational note (auto-logged to the timeline)
 *   • upload a supporting document (reuses the existing document
 *     pipeline — no parallel storage system)
 *   • mark the step complete  (+ auto-advance the deal stage if
 *     this step is the active one)
 *
 * Every primary action writes an immutable activity row, so the
 * operational timeline IS the source of truth — there is no
 * parallel state to keep in sync. The sheet is intentionally NOT
 * a workflow editor; for full milestone admin the operator still
 * uses the Progress Deal sheet from the action bar.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  X, Loader2, Calendar, Paperclip, MessageSquare, CheckCircle2,
  ChevronDown, Star,
} from "lucide-react";
import { api, apiError, apiErrorCode } from "../../lib/api";
import { toast } from "sonner";
import { useChainStore } from "../../data/chainStore";

/* iter-377 — "Complete when" descriptions.
 * A tight, plain-English phrase for each rail step that tells the
 * operator what "done" actually means. Falls back to a generic
 * "when the milestone is fully actioned" when a step isn't listed
 * (custom / bespoke milestones). Keeping this map in the sheet
 * rather than backend meta so product can iterate copy without a
 * deploy. */
const COMPLETE_WHEN = {
  offer:          "The offer is agreed and the transaction can begin.",
  memo:           "Memorandum of Sale has been issued to all parties.",
  aml:            "AML checks on buyer and seller have cleared.",
  mortgage:       "Buyer has a valid mortgage offer in hand.",
  mortgage_app:   "Mortgage application has been submitted.",
  mortgage_offer: "Mortgage offer has been received by the buyer.",
  survey:         "Buyer's survey has been booked or completed.",
  contract:       "Seller solicitor has issued the draft contract.",
  contracts:      "Seller solicitor has issued the draft contract.",
  searches:       "Local authority searches have been ordered.",
  mgmt_pack:      "Leasehold management pack has been received.",
  bsa:            "Building Safety Act review is signed off.",
  enquiries:      "Buyer's solicitor has issued the first set of enquiries.",
  exchange:       "Contracts have been exchanged with all parties.",
  exchange_ready: "Contracts have been exchanged with all parties.",
  completion:     "Completion funds have transferred and keys released.",
  complete:       "Completion funds have transferred and keys released.",
};

function fmtIsoDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch { return null; }
}

function isoToInputValue(iso) {
  if (!iso) return "";
  // Accepts both `YYYY-MM-DD` and full ISO.
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  } catch { return ""; }
}

export function MilestoneProgressionSheet({
  open,
  step,            // { id, label, state, target_date }
  dealId,
  currentStepId,   // id of the journey step that is currently active
  milestones,      // iter-alignment · P0 · Milestone-Level Completion — `{items, can_complete}` from parent
  onClose,
  onProgressed,    // () → void · parent reloads journey + activity
}) {
  const [busy, setBusy] = useState(false);
  const [dateValue, setDateValue] = useState("");
  const [note, setNote] = useState("");
  const [pendingFile, setPendingFile] = useState(null);
  /* iter-377 — progressive disclosure. Each optional action is
   * collapsed by default so the sheet reads as a tight "one-click
   * complete" surface; expanding a row shows the inline editor. */
  const [showUpdate, setShowUpdate] = useState(false);
  const [showDocument, setShowDocument] = useState(false);
  const [showTarget, setShowTarget] = useState(false);
  const fileInputRef = useRef(null);
  // iter-270F — Pilot deals are frontend-only; Mark Complete persists
  // through chainStore instead of the API.
  const { findDeal, markPilotJourneyStepComplete } = useChainStore();
  const _deal = findDeal?.(dealId);
  const _isPilot = !!(_deal && _deal._pilot);

  /* iter-alignment · P0 · Milestone-Level Completion (2026-08-18)
   * -----------------------------------------------------------
   * The sheet used to POST `/journey/{step.id}/complete` — a
   * whole-stage write that made the modal read "Mortgage" while the
   * checklist inside the same page showed six pending rows.  The
   * refactor pivots the sheet onto the SAME milestone SSOT the
   * checklist rows and the Confirm Exchange / Completion gates
   * already use: we resolve the current outstanding required
   * milestone within `step.id`, act ON THAT ROW, and let the
   * backend's existing auto-advance close the stage when the last
   * required row lands.  Terminal legal-event rows
   * (`contracts_exchanged` for exchange · `completion_date` for
   * complete) are deliberately EXCLUDED — the Confirm Exchange /
   * Confirm Completion gates on the Current Stage panel own those
   * events and must never be auto-fired from here. */
  const TERMINAL_MILESTONES = { exchange: "contracts_exchanged", complete: "completion_date" };
  const isMilestoneDone = (m) => !!m && (m.state === "done" || m.state === "complete" || !!m.completed_at);
  const stageMilestones = useMemo(() => {
    const items = Array.isArray(milestones?.items) ? milestones.items : [];
    return step?.id ? items.filter((m) => m?.stage === step.id) : [];
  }, [milestones, step]);
  const requiredMilestones = useMemo(
    () => stageMilestones.filter((m) => m?.optional !== true),
    [stageMilestones],
  );
  const terminalId = step?.id ? TERMINAL_MILESTONES[step.id] || null : null;
  const outstandingMilestones = useMemo(
    () => requiredMilestones.filter((m) => !isMilestoneDone(m) && m.id !== terminalId),
    [requiredMilestones, terminalId],
  );
  const currentMilestone = outstandingMilestones[0] || null;

  useEffect(() => {
    if (open && step) {
      // Milestone-level completion: reset the target-date input to
      // the CURRENT milestone's target_date (not the stage's).  On
      // stages without a resolvable milestone (pilot / bespoke)
      // fall back to the step target so the input stays useful.
      const seedIso = currentMilestone?.target_date || step.target_date;
      setDateValue(isoToInputValue(seedIso));
      setNote("");
      setPendingFile(null);
      setShowUpdate(false);
      setShowDocument(false);
      setShowTarget(false);
      setBusy(false);
    }
  }, [open, step, currentMilestone?.id]);

  // Esc to close.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, busy, onClose]);

  const safeReload = useCallback(() => {
    try { onProgressed?.(); } catch { /* never throw from caller */ }
  }, [onProgressed]);

  /* iter-alignment · P0 · Complete Milestone Precision
   * ---------------------------------------------------
   * (2026-02-19) — Clicking "Complete milestone" MUST only
   * complete the specific canonical milestone open in the
   * modal — never the whole stage.  The previous version
   * fell back to `POST /journey/{step.id}/complete` (a
   * whole-stage write) whenever `currentMilestone` didn't
   * resolve, which could silently close a full stage on a
   * single click.  We now short-circuit with an error toast
   * in that edge case so the operator sees exactly what
   * happened; the backend
   * `POST /milestones/{id}/complete` remains the ONLY write
   * path, and stage advancement is owned by the backend's
   * canonical rules (`_TERMINAL_CONFIRMATION_MILESTONES` +
   * `reconcile_deal_status`).
   *
   * `busy` is set BEFORE the async work begins and reset in
   * `finally` so duplicate submits during network wait are
   * impossible.  On success we explicitly call `onClose()`
   * so the modal never lingers optimistically; on failure
   * the toast surfaces the API error and the milestone stays
   * un-completed. */
  const completeCurrentMilestone = useCallback(async () => {
    if (busy || !step) return;
    setBusy(true);
    const trimmedNote = note.trim();
    const initialDate = isoToInputValue(currentMilestone?.target_date || step.target_date);
    const dateChanged = (dateValue || "") !== initialDate;
    try {
      if (_isPilot) {
        const res = markPilotJourneyStepComplete?.(dealId, step.id);
        toast.success("Milestone completed", {
          description: res?.newStage
            ? `Stage advanced to ${res.newStage.replace(/_/g, " ")}.`
            : undefined,
          icon: "✓",
        });
        safeReload();
        onClose();
        return;
      }
      // Precondition: a live deal MUST resolve a canonical
      // current milestone.  Without it, we refuse the click —
      // the whole-stage fallback that used to run here has been
      // retired.  This can only happen in the brief window
      // between the sheet opening and the auto-close useEffect
      // firing, so an error toast is honest without being
      // scary.
      if (!currentMilestone) {
        toast.error("No milestone to complete", {
          description: "The current stage has no outstanding required milestones.",
        });
        return;
      }
      // 1. Persist target date if the operator edited it (journey scope).
      if (dateChanged) {
        await api
          .post(`/api/v2/deals/${dealId}/journey/${step.id}/target`, {
            target_date: dateValue || null,
          })
          .catch((err) => toast.error("Target date not saved", { description: apiError(err) }));
      }
      // 2. Persist "What's changed?" note if any (journey scope).
      if (trimmedNote) {
        await api
          .post(`/api/v2/deals/${dealId}/journey/${step.id}/note`, { body: trimmedNote })
          .catch((err) => toast.error("Update not saved", { description: apiError(err) }));
      }
      // 3. Upload attached document if any.
      if (pendingFile) {
        const fd = new FormData();
        fd.append("file", pendingFile, pendingFile.name);
        fd.append("journey_step", step.id);
        fd.append("milestone_label", currentMilestone.label || step.label);
        await api
          .post(`/api/deals/${dealId}/documents`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          })
          .catch((err) => toast.error("Document not uploaded", { description: apiError(err) }));
      }
      // 4. Fire the primary milestone-level completion — this is
      //    the ONLY write path.  Backend enforces stage
      //    advancement only for terminal confirmation milestones
      //    (contracts_exchanged / completion_date); every other
      //    stage stays In Progress until every required row lands
      //    and the operator (or the backend's reconciliation)
      //    marks the stage complete.
      await api.post(`/api/v2/deals/${dealId}/milestones/${currentMilestone.id}/complete`);
      toast.success("Milestone completed", {
        description: currentMilestone.label,
        icon: "✓",
      });
      // 5. Reconcile the Deal Room: the parent's `onProgressed()`
      //    refetches milestones, journey, activities and
      //    progress counts — the checklist, journey rail,
      //    Transaction Progress ring and Current Stage panel
      //    all pick up the new state in the same tick.
      safeReload();
      // 6. Close the modal per the operator brief so the Deal
      //    Room's own state becomes the source of truth
      //    immediately after the write.  Re-opening the sheet
      //    on the next outstanding milestone is a single click
      //    from the rail.
      onClose();
    } catch (err) {
      // Premature exchange (409 exchange_not_ready): lead with the actionable required-items message.
      if (apiErrorCode(err) === "exchange_not_ready") toast.error(apiError(err));
      else toast.error("Couldn't complete milestone", { description: apiError(err) });
    } finally {
      setBusy(false);
    }
  }, [busy, dealId, step, currentMilestone, note, dateValue, pendingFile, _isPilot, markPilotJourneyStepComplete, safeReload, onClose]);

  /* iter-alignment · P0 · Milestone-Level Completion — auto-close.
   * Once the parent reload flows the fresh milestones down we
   * re-derive `currentMilestone`.  When it resolves to null AND
   * every OTHER required row is done we close the sheet — that
   * signal means either the whole stage is done (backend has
   * already advanced) OR only the terminal legal-event row
   * remains, at which point the Current Stage panel's Confirm
   * gate owns the flow.  We guard on `!busy` so the burst
   * between the POST returning and `onProgressed()` completing
   * doesn't fire a stray close. */
  // Opened on a stage with nothing left to action (e.g. a recorded stage tapped on the journey):
  // show a read-only summary instead of closing. Fixed per opening, so completing the last row still auto-closes.
  const _session = useRef({ key: null, summary: false });
  const _sessionKey = open && step ? step.id : null;
  if (_session.current.key !== _sessionKey) {
    // Key Dates rows (phone) always open the read-only stage details view.
    const _forceSummary = !!_sessionKey && !!step?.summary;
    _session.current = { key: _sessionKey, summary: !!_sessionKey && Array.isArray(milestones?.items) && !currentMilestone || _forceSummary };
  }
  const summaryMode = _session.current.summary;

  useEffect(() => {
    if (!open || busy || summaryMode) return;
    // Pilot / bespoke deals with no resolvable milestone list
    // keep the legacy behaviour (single-click closes on complete).
    if (!Array.isArray(milestones?.items)) return;
    if (currentMilestone) return;
    // No outstanding non-terminal milestones — safe to close.
    onClose();
  }, [open, busy, summaryMode, milestones, currentMilestone, onClose]);

  if (!open || !step) return null;
  if (typeof document === "undefined") return null;

  // iter-alignment · P0 · Milestone-Level Completion — the sheet
  // header shows the OUTSTANDING milestone, not the whole stage.
  // Fall back to `step.label` when milestones aren't resolvable
  // (pilot / bespoke stages) so the sheet still makes sense.
  const headerLabel = summaryMode ? step.label : (currentMilestone?.label || step.label);
  const completeWhen =
    (currentMilestone && (currentMilestone.complete_when || currentMilestone.description
      || `${currentMilestone.label} — confirmed and recorded for this transaction.`))
    || COMPLETE_WHEN[step.id]
    || "The milestone is fully actioned.";

  return createPortal(
    <div
      className="fixed inset-0 z-[85] flex items-end sm:items-center justify-end sm:justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="journey-progress-title"
      data-testid="milestone-progression-sheet"
    >
      <button
        type="button"
        aria-label="Dismiss"
        onClick={busy ? undefined : onClose}
        className="absolute inset-0 bg-slate-900/35 backdrop-blur-[3px]"
        data-testid="milestone-progression-sheet-backdrop"
        style={{ animation: "cf-mps-fade 200ms ease-out" }}
      />
      <aside
        className="relative w-full sm:max-w-[460px] bg-white shadow-[0_24px_60px_-20px_rgba(15,23,42,0.30)] rounded-t-3xl sm:rounded-2xl ring-1 ring-slate-200 sm:mr-6 max-h-[92dvh] flex flex-col"
        style={{ animation: "cf-mps-in 260ms cubic-bezier(0.16,1,0.3,1)" }}
      >
        {/* Header — Current milestone pill · title · close */}
        <header className="flex items-start justify-between gap-3 px-6 pt-5 pb-2">
          <div className="min-w-0">
            <span
              className="inline-flex items-center gap-1.5 px-2.5 h-[24px] rounded-md text-[10.5px] font-bold bg-violet-50 text-violet-700 ring-1 ring-violet-200/70"
              data-testid="milestone-progression-sheet-state"
            >
              <Star size={11} strokeWidth={2.4} className="text-violet-500" fill="currentColor" />
              {summaryMode ? "Stage details" : (!currentStepId || step.id === currentStepId ? "Current milestone" : "Outstanding in this stage")}
            </span>
            <h2
              id="journey-progress-title"
              className="text-[20px] font-bold text-slate-900 tracking-tight leading-tight mt-3"
              data-testid="milestone-progression-sheet-title"
            >
              {headerLabel}
            </h2>
          </div>
          <button
            type="button"
            onClick={busy ? undefined : onClose}
            disabled={busy}
            className="inline-flex w-8 h-8 items-center justify-center rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 disabled:opacity-50"
            aria-label="Close"
            data-testid="milestone-progression-sheet-close"
          >
            <X size={18} />
          </button>
        </header>

        {summaryMode && (
          <div className="px-6 pb-4 overflow-y-auto" data-testid="milestone-progression-summary">
            <ul className="mt-2 divide-y divide-slate-100 border-y border-slate-100">
              {stageMilestones.map((m) => {
                const done = isMilestoneDone(m);
                const when = done ? fmtIsoDate(m.completed_at) : null;
                return (
                  <li key={m.id} className="py-2.5 flex items-start justify-between gap-3 text-[13.5px]" data-testid={`milestone-progression-summary-row-${m.id}`}>
                    <span className="text-slate-800">{m.label}{m.optional ? <span className="text-slate-400"> · optional</span> : null}</span>
                    <span className={`shrink-0 ${done ? "text-slate-600" : "text-slate-400"}`}>
                      {done ? (when ? `Recorded ${when}` : "Not recorded") : m.id === terminalId ? "Confirmed from the stage panel" : m.optional ? "Optional, not done" : "Outstanding"}
                    </span>
                  </li>
                );
              })}
              {stageMilestones.length === 0 && <li className="py-2.5 text-[13.5px] text-slate-500">No checklist items for this stage.</li>}
            </ul>
          </div>
        )}
        {/* Scrollable body */}
        <div className={`px-6 pb-2 overflow-y-auto ${summaryMode ? "hidden" : ""}`} data-testid="milestone-progression-sheet-body">
          {/* Complete when — short one-liner explaining what "done" means. */}
          <section className="mt-2 mb-1" data-testid="milestone-progression-complete-when">
            <p className="text-[13.5px] font-semibold text-violet-700">Complete when:</p>
            <p className="text-[14px] text-slate-800 leading-snug mt-0.5">{completeWhen}</p>
          </section>

          {/* Progressive-disclosure options — Add update / Add document / Set target date */}
          <div className="mt-4 divide-y divide-slate-100 border-t border-slate-100">
            {/* + Add update */}
            <ExpandableRow
              testId="milestone-progression-row-update"
              icon={<MessageSquare size={16} strokeWidth={2.2} className="text-violet-600" />}
              title="Add update"
              subtitle="Add a progress update or note (optional)"
              open={showUpdate}
              onToggle={() => setShowUpdate((v) => !v)}
            >
              <div className="pt-1">
                <p className="text-[11px] uppercase tracking-[0.10em] font-bold text-slate-500 mb-1.5">
                  What&apos;s changed?
                </p>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. Buyer's solicitor confirmed enquiries were sent this morning."
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg ring-1 ring-slate-200 focus:ring-2 focus:ring-violet-300 outline-none text-[13px] text-slate-900 placeholder:text-slate-400 transition resize-none"
                  data-testid="milestone-progression-note-input"
                  maxLength={600}
                />
              </div>
            </ExpandableRow>

            {/* + Add document */}
            <ExpandableRow
              testId="milestone-progression-row-document"
              icon={<Paperclip size={16} strokeWidth={2.2} className="text-violet-600" />}
              title="Add document"
              subtitle="Upload or attach a document (optional)"
              open={showDocument}
              onToggle={() => setShowDocument((v) => !v)}
            >
              <div className="pt-1 flex items-center gap-2">
                {pendingFile ? (
                  <div
                    className="flex-1 inline-flex items-center gap-2 h-10 px-3 rounded-lg bg-emerald-50/60 ring-1 ring-emerald-200/70 text-[12.5px] text-emerald-800"
                    data-testid="milestone-progression-file-chip"
                  >
                    <CheckCircle2 size={14} strokeWidth={2.4} className="text-emerald-600" />
                    <span className="truncate max-w-[240px]">{pendingFile.name}</span>
                    <button
                      type="button"
                      onClick={() => { setPendingFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                      className="ml-auto text-[11px] font-semibold text-emerald-700 hover:text-emerald-900"
                      data-testid="milestone-progression-file-clear"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={busy}
                    className="w-full inline-flex items-center justify-center gap-2 h-10 rounded-lg bg-white hover:bg-slate-50 ring-1 ring-dashed ring-slate-200 text-[12.5px] font-semibold text-slate-700 transition disabled:opacity-50"
                    data-testid="milestone-progression-upload-trigger"
                  >
                    <Paperclip size={14} strokeWidth={2.2} className="text-violet-600" />
                    Choose a file
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  data-testid="milestone-progression-upload-input"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) setPendingFile(f);
                  }}
                />
              </div>
            </ExpandableRow>

            {/* + Set target date */}
            <ExpandableRow
              testId="milestone-progression-row-target"
              icon={<Calendar size={16} strokeWidth={2.2} className="text-violet-600" />}
              title="Set target date"
              subtitle="Set an expected completion date (optional)"
              open={showTarget}
              onToggle={() => setShowTarget((v) => !v)}
            >
              <div className="pt-1">
                <input
                  type="date"
                  value={dateValue}
                  onChange={(e) => setDateValue(e.target.value)}
                  className="w-full h-10 px-3 rounded-lg ring-1 ring-slate-200 focus:ring-2 focus:ring-violet-300 outline-none text-[13px] text-slate-900 transition"
                  data-testid="milestone-progression-target-input"
                />
                {dateValue && (
                  <button
                    type="button"
                    onClick={() => setDateValue("")}
                    className="mt-1.5 text-[11.5px] font-semibold text-slate-500 hover:text-slate-700"
                    data-testid="milestone-progression-target-clear"
                  >
                    Clear date
                  </button>
                )}
              </div>
            </ExpandableRow>
          </div>
        </div>

        {/* Footer — Cancel · Mark complete */}
        <footer className="mt-auto px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-3 bg-white/90">
          <button
            type="button"
            onClick={busy ? undefined : onClose}
            disabled={busy}
            className="h-11 px-5 rounded-lg text-[13px] font-semibold text-slate-600 hover:text-slate-900 disabled:opacity-50"
            data-testid="milestone-progression-cancel"
          >
            {summaryMode ? "Close" : "Cancel"}
          </button>
          {!summaryMode && <button
            type="button"
            onClick={completeCurrentMilestone}
            disabled={busy || (!_isPilot && !currentMilestone)}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-[13.5px] font-semibold transition disabled:opacity-60 disabled:cursor-not-allowed shadow-[0_8px_20px_-8px_rgba(124,58,237,0.55)]"
            data-testid="milestone-progression-mark-complete"
          >
            {busy ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <CheckCircle2 size={16} strokeWidth={2.4} />
            )}
            Complete milestone
          </button>}
        </footer>

        <style>{`
          @keyframes cf-mps-fade { from { opacity: 0; } to { opacity: 1; } }
          @keyframes cf-mps-in   { from { transform: translateY(24px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
          @media (max-width: 640px) {
            aside { max-height: 92dvh; }
            @keyframes cf-mps-in { from { transform: translateY(100%); } to { transform: translateY(0); } }
          }
        `}</style>
      </aside>
    </div>,
    document.body,
  );
}

/* iter-377 — Lightweight progressive-disclosure row.
 * Renders a "+ Title" clickable header with a subtitle and expands
 * inline to show the passed-in editor when clicked. Keeps every
 * optional action collapsed by default so most milestones can be
 * completed in a single click without ever opening a form. */
function ExpandableRow({ testId, icon, title, subtitle, open, onToggle, children }) {
  return (
    <div className="py-3.5" data-testid={testId} data-open={open ? "1" : "0"}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 text-left group"
        aria-expanded={open}
      >
        <span className="inline-flex w-10 h-10 items-center justify-center rounded-xl bg-violet-50 ring-1 ring-violet-100 shrink-0">
          {icon}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[14px] font-semibold text-violet-700 group-hover:text-violet-900">
            + {title}
          </span>
          <span className="block text-[12.5px] text-slate-500 leading-snug">
            {subtitle}
          </span>
        </span>
        <ChevronDown
          size={16}
          strokeWidth={2.2}
          className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="pl-13 pt-3" style={{ paddingLeft: "3.25rem" }}>
          {children}
        </div>
      )}
    </div>
  );
}

export default MilestoneProgressionSheet;
